<script setup lang="ts">
/**
 * **文档管理窗口**（内核编辑器 `documents`）—— 也当首页用。
 *
 * ```
 *   新建 / 导入        上面那条工具条
 *   列表               最近改过的在前：名字 · UUID · 节点数 · 时间
 *   每一份能干什么      打开 / 重命名 / 复制 / 导出 / 删除
 * ```
 *
 * ## ⚠️ 它不认识文档库的实现
 *
 * 组件只收**数据 + 一组动作**（`actions`）：库里怎么存、UUID 怎么生成、
 * 打开时弹不弹窗，全在宿主（`useDocuments` + `graph/documentLibrary.ts`）。
 * 这条分工不是洁癖 —— 它是这个窗口能被 `verify:nodeui` 那样的
 * 组件级单测直接挂起来验的前提（不用起整个 Playground）。
 *
 * ## ⚠️ 重命名是**就地改**（不是弹窗）
 *
 * 一张列表里改名，弹窗是最烦的那种形态（挡住列表、改完看不见上下文）。
 * 所以点「重命名」那一行的名字变成输入框：Enter / 失焦提交，Esc 取消。
 * 名字立刻写进文档库 —— 和"代码边打边存"同一条纪律：**别让人担心丢**。
 */
import { computed, nextTick, ref } from 'vue'
import type { DocumentRecord } from '@/types/document'

const props = defineProps<{
  /** 库读完了吗（IndexedDB 是异步的，见 `useDocuments.ready`） */
  ready: boolean
  /** 文档存在哪儿（`indexeddb` / `localstorage` / `memory`） */
  storeKind: 'indexeddb' | 'localstorage' | 'memory'
  /** 库里的文档（宿主已排好序） */
  documents: DocumentRecord[]
  /** 当前正打开的那一份（没有 = 这份画布还不属于任何文档） */
  currentId: string | null
  /** 当前文档名（没身份时是宿主的默认名） */
  currentName: string
  /** 画布改过、还没存进库里 */
  dirty: boolean
  /** 统计（界面上那句"多大"） */
  summary: { count: number; nodes: number; edges: number }
  /**
   * 问一句（删除是不可撤销的）。
   *
   * ⚠️ **注入而不是直接 `window.confirm`**：组件要能在 happy-dom 里挂起来
   * 验（那个环境**没有实现 confirm**，直接调会抛错）。
   */
  /**
   * **问一句**（注入，不是 `window.confirm`）。
   *
   * ⚠️ 返回 `Promise`：宿主用的是**画面弹窗**（`NpInfoDialog`）。
   * 而且组件要能在 happy-dom 里挂起来验 —— 那个环境**没有实现 confirm**，
   * 直接调会抛错。
   */
  ask: (message: string) => Promise<boolean>
  /** 动作由宿主注入 —— 组件不认识文档库 */
  actions: {
    create: () => void
    open: (id: string) => void
    save: () => void
    rename: (id: string, name: string) => void
    duplicate: (id: string) => void
    remove: (id: string) => void
    exportFile: (id: string) => void
    importFile: () => void
  }
}>()

/** 正在改名的那一份 */
const editingId = ref<string | null>(null)
const draftName = ref('')
const nameEl = ref<HTMLInputElement | null>(null)

/**
 * ⚠️ 用**函数 ref**，不用 `ref="nameEl"` 字符串形式：输入框在 `v-for` 里，
 * 而"v-for 里的 ref 会收集成数组"这条规矩会让 `nameEl.value` 变成一个
 * 数组 → `.focus()` 直接抛 TypeError（改名的焦点就没了，而错误在
 * Vue 的渲染里一闪而过，很难查）。
 * 只记**非空**那一次：切换改名目标时旧元素会以 `null` 回调一次，
 * 那一下不该把新元素抹掉。
 */
function setNameEl(el: unknown): void {
  if (el) nameEl.value = el as HTMLInputElement
}

async function startRename(record: DocumentRecord): Promise<void> {
  editingId.value = record.id
  draftName.value = record.name
  await nextTick()
  nameEl.value?.focus()
  nameEl.value?.select()
}

function commitRename(): void {
  const id = editingId.value
  if (!id) return
  const name = draftName.value
  editingId.value = null
  const record = props.documents.find((item) => item.id === id)
  /* 没改就不写（免得白刷一次 updatedAt，把列表顺序搅乱） */
  if (record && record.name !== name.trim()) props.actions.rename(id, name)
}

function cancelRename(): void {
  editingId.value = null
}

/** 确认删除：这一步**不可撤销**（历史里没有"库"这一层） */
async function askRemove(record: DocumentRecord): Promise<void> {
  const ok = await props.ask(
    `从文档库里删掉「${record.name}」？\n这份文档的图会一起删掉，不能撤销。`,
  )
  if (!ok) return
  props.actions.remove(record.id)
}

/** UUID 太长，列表里给前 8 位；完整的那串放在 title 里（能复制） */
function shortId(id: string): string {
  return id.length > 8 ? id.slice(0, 8) : id
}

function when(ms: number): string {
  const date = new Date(ms)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const currentRecord = computed(() => props.documents.find((item) => item.id === props.currentId))

/**
 * 「存在哪儿」那句话 —— **必须说出来**。
 *
 * 用户的图在哪儿是件正经事：IndexedDB 能装几百 MB，localStorage 只有 5MB，
 * 而内存档刷新就没了。三种档位在界面上长得一样、行为却完全不同，
 * 不写清楚的话"我的文档怎么没了"就没法回答。
 */
const storeLabel = computed(() => {
  if (props.storeKind === 'indexeddb') return '浏览器的 IndexedDB（容量按磁盘算，比 localStorage 大得多）'
  if (props.storeKind === 'localstorage') return '浏览器的 localStorage（约 5MB；图片大的文档容易撑满）'
  return '内存（这次改动没能存下来：这个环境没有可用的本地存储，先「导出」成文件）'
})
</script>

<template>
  <div class="pg__docs" data-np-documents>
    <header class="pg__docs-bar">
      <button class="pg__docs-act is-primary" type="button" data-np-doc-create @click="actions.create()">
        ＋ 新建文档
      </button>
      <button class="pg__docs-act" type="button" data-np-doc-import @click="actions.importFile()">
        导入文件…
      </button>
      <button
        class="pg__docs-act"
        type="button"
        data-np-doc-save
        :disabled="!dirty && Boolean(currentRecord)"
        :title="currentId ? '把当前画布存进文档库' : '把当前画布存成一份新文档'"
        @click="actions.save()"
      >
        保存到文档库
      </button>
      <span class="pg__docs-spacer" />
      <span class="pg__docs-stat" data-np-doc-stats>
        {{ summary.count }} 份 · {{ summary.nodes }} 节点 / {{ summary.edges }} 连线
      </span>
    </header>

    <!--
      ⚠️ 当前文档这一条**单独放在列表上面**：列表可能很长（滚动），
      而"我现在在编的是哪一份、存没存"必须一直看得见。
    -->
    <section class="pg__docs-current" data-np-doc-current>
      <span class="pg__docs-key">当前</span>
      <span class="pg__docs-cur-name">{{ currentName }}</span>
      <span v-if="dirty" class="pg__docs-chip is-dirty" data-np-doc-dirty>未保存</span>
      <span v-else-if="currentRecord" class="pg__docs-chip">已保存</span>
      <span v-else class="pg__docs-chip is-mute">未入库</span>
      <code v-if="currentId" class="pg__docs-cur-id" :title="currentId">{{ currentId }}</code>
    </section>

    <p v-if="!ready" class="pg__docs-empty" data-np-doc-loading>正在读文档库…</p>

    <p v-else-if="!documents.length" class="pg__docs-empty" data-np-doc-none>
      库里还没有文档。<br />
      点「新建文档」把当前画布收进库里，或者「导入文件…」把以前存下来的
      <code>.json</code> 收进来。<br />
      也可以去 <strong>文件 → 示例文件</strong> 打开一份示例玩玩。
    </p>

    <ul v-else class="pg__docs-grid" data-np-doc-grid>
      <li
        v-for="record in documents"
        :key="record.id"
        class="pg__docs-card"
        :class="{ 'is-current': record.id === currentId }"
        :data-np-doc-id="record.id"
      >
        <div class="pg__docs-head">
          <input
            v-if="editingId === record.id"
            :ref="setNameEl"
            v-model="draftName"
            class="pg__docs-name-input"
            data-np-doc-rename-input
            spellcheck="false"
            @keydown.enter.prevent="commitRename()"
            @keydown.esc.prevent="cancelRename()"
            @blur="commitRename()"
          />
          <button
            v-else
            class="pg__docs-name"
            type="button"
            title="双击改名"
            @dblclick="startRename(record)"
          >
            {{ record.name }}
          </button>
          <span v-if="record.id === currentId" class="pg__docs-chip">当前</span>
        </div>

        <!--
          ⚠️ 卡片上给一个**一眼能认出来**的缩略块：节点数做成大小不同的方块
          （不是真缩略图 —— 画布渲染成本太高，而且列表里要的是"认得出"）。
        -->
        <div class="pg__docs-thumb" :data-np-doc-nodes="record.document.nodes.length">
          <span
            v-for="i in Math.min(record.document.nodes.length, 12)"
            :key="i"
            class="pg__docs-dot"
          />
          <span v-if="record.document.nodes.length > 12" class="pg__docs-dot is-more">+</span>
        </div>

        <div class="pg__docs-meta">
          <code :title="`文档 UUID：${record.id}`">{{ shortId(record.id) }}</code>
          <span>{{ record.document.nodes.length }} 节点</span>
          <span>{{ record.document.edges.length }} 连线</span>
          <span class="pg__docs-when">{{ when(record.updatedAt) }}</span>
        </div>

        <div class="pg__docs-actions">
          <button
            class="pg__docs-mini"
            type="button"
            :title="
              record.id === currentId
                ? '这一份正开着 —— 点它把画布摆到眼前'
                : '打开这一份（会替换当前画布）'
            "
            @click="actions.open(record.id)"
          >
            打开
          </button>
          <button class="pg__docs-mini" type="button" @click="startRename(record)">重命名</button>
          <button class="pg__docs-mini" type="button" @click="actions.duplicate(record.id)">复制</button>
          <button class="pg__docs-mini" type="button" @click="actions.exportFile(record.id)">导出</button>
          <button class="pg__docs-mini is-danger" type="button" @click="askRemove(record)">
            删除
          </button>
        </div>
      </li>
    </ul>

    <p class="pg__docs-note" data-np-doc-store>
      存在<strong>{{ storeLabel }}</strong
      >。图片是内嵌进文档的，存不下时会<strong>明确提示</strong>，不会静默丢掉。
    </p>
  </div>
</template>

<style scoped>
.pg__docs {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
  padding: 10px 12px;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: var(--np-surface-0);
  color: var(--np-panel-text);
  font-size: 11px;
}

/* ── 顶部工具条 ── */

.pg__docs-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.pg__docs-act {
  height: var(--np-ctl-h-sm);
  padding: 0 8px;
  border: 1px solid var(--np-border-strong);
  border-radius: var(--np-radius-s);
  background: var(--np-panel-input-bg);
  color: var(--np-panel-text);
  font: inherit;
  font-size: 10.5px;
  line-height: 1;
  cursor: pointer;
}

.pg__docs-act:hover:not(:disabled) {
  border-color: var(--np-accent);
  color: var(--np-accent);
}

.pg__docs-act:disabled {
  opacity: 0.45;
  cursor: default;
}

.pg__docs-act.is-primary {
  border-color: var(--np-accent);
  color: var(--np-accent);
}

.pg__docs-spacer {
  flex: 1 1 auto;
}

.pg__docs-stat {
  color: var(--np-panel-mute);
  font-size: 10px;
  font-variant-numeric: tabular-nums;
}

/* ── 当前文档 ── */

.pg__docs-current {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  border: 1px solid var(--np-border-soft);
  border-radius: var(--np-radius-m);
  background: var(--np-surface-1);
}

.pg__docs-key {
  color: var(--np-panel-dim);
  font-size: 10px;
}

.pg__docs-cur-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
}

.pg__docs-cur-id {
  margin-left: auto;
  color: var(--np-panel-mute);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 9.5px;
}

.pg__docs-chip {
  flex: 0 0 auto;
  padding: 0 5px;
  border-radius: var(--np-radius-s);
  background: var(--np-panel-chip-bg);
  color: var(--np-panel-mute);
  font-size: 9.5px;
  line-height: 15px;
}

.pg__docs-chip.is-dirty {
  background: var(--np-accent-soft);
  color: var(--np-accent);
}

.pg__docs-chip.is-mute {
  opacity: 0.7;
}

/* ── 卡片网格 ──────────────────────────────────────────────
 *
 * 用户要的"卡片式显示"：一行塞得下几张就塞几张（`auto-fill` + 最小宽度），
 * 窗口拉宽自动多排一列、手机上一列。
 * ⚠️ 不用固定列数：文档窗口既能当整屏首页（宽），也能挤在分屏的一角（窄）。
 */

.pg__docs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
  align-content: start;
}

.pg__docs-card {
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 8px 9px 9px;
  border: 1px solid var(--np-border-soft);
  border-top: 2px solid var(--np-border-strong);
  border-radius: var(--np-radius-m);
  background: var(--np-surface-1);
  transition: border-color var(--np-motion-fast) ease, background-color var(--np-motion-fast) ease;
}

.pg__docs-card:hover {
  border-color: var(--np-border-strong);
  background: var(--np-surface-2);
}

/* 当前那一份：顶边用强调色 + 一点点底色 —— 一屏几十张卡片时一眼找得到自己 */
.pg__docs-card.is-current {
  border-top-color: var(--np-accent);
  background: var(--np-surface-2);
}

/* 缩略块：节点数做成小方块（认得出"这份有多少东西"） */
.pg__docs-thumb {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  min-height: 16px;
  padding: 4px;
  border-radius: var(--np-radius-s);
  background: var(--np-surface-0);
}

.pg__docs-dot {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background: var(--np-border-strong);
}

.pg__docs-dot.is-more {
  width: auto;
  height: 8px;
  padding: 0 2px;
  background: transparent;
  color: var(--np-panel-mute);
  font-size: 9px;
  line-height: 8px;
}

.pg__docs-head {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.pg__docs-name {
  min-width: 0;
  flex: 1 1 auto;
  padding: 1px 3px;
  border: 1px solid transparent;
  border-radius: var(--np-radius-s);
  background: transparent;
  color: var(--np-panel-text);
  font: inherit;
  font-size: 11.5px;
  font-weight: 600;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}

.pg__docs-name:hover {
  border-color: var(--np-border-strong);
  background: var(--np-panel-input-bg);
}

.pg__docs-name-input {
  flex: 1 1 auto;
  min-width: 0;
  height: var(--np-ctl-h-sm);
  padding: 0 5px;
  border: 1px solid var(--np-accent);
  border-radius: var(--np-radius-s);
  background: var(--np-panel-input-bg);
  color: var(--np-panel-text);
  font: inherit;
  font-size: 11.5px;
  outline: none;
}

.pg__docs-meta {
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
  color: var(--np-panel-mute);
  font-size: 10px;
}

/* ⚠️ 时间靠右（`margin-left: auto`）：卡片窄的时候它会换到第二行，
   靠右仍然让它像"元信息的尾巴"，而不是又一个标签 */
.pg__docs-when {
  margin-left: auto;
  font-variant-numeric: tabular-nums;
}

.pg__docs-meta code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 9.5px;
}

.pg__docs-actions {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.pg__docs-mini {
  height: 18px;
  padding: 0 7px;
  border: 1px solid var(--np-border-strong);
  border-radius: var(--np-radius-s);
  background: transparent;
  color: var(--np-panel-dim);
  font: inherit;
  font-size: 10px;
  line-height: 1;
  cursor: pointer;
}

.pg__docs-mini:hover:not(:disabled) {
  border-color: var(--np-accent);
  color: var(--np-accent);
}

.pg__docs-mini:disabled {
  opacity: 0.4;
  cursor: default;
}

.pg__docs-mini.is-danger:hover {
  border-color: var(--np-danger, #d96a6a);
  color: var(--np-danger, #d96a6a);
}

/* ── 空状态 / 脚注 ── */

.pg__docs-empty {
  margin: 0;
  padding: 14px 4px;
  color: var(--np-panel-mute);
  line-height: 1.8;
  text-align: center;
}

.pg__docs-note {
  margin: auto 0 0;
  padding-top: 8px;
  color: var(--np-panel-mute);
  font-size: 10px;
  line-height: 1.6;
}
</style>
